import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  X,
} from "lucide-react";

import Reveal from "@/components/public/Reveal";
import BeforeAfterSlider from "@/components/public/BeforeAfterSlider";
import { publicApi } from "@/lib/api";

const BASE_TABS = [
  { key: "motivation", label: "Motivation" },
  { key: "summary", label: "Summary" },
  { key: "details", label: "Details" },
  { key: "design_team", label: "Design Team" },
];

function getDetailExtraRows(extra) {
  if (!extra) return [];

  if (Array.isArray(extra)) {
    return extra
      .map((item) => ({
        label: item?.label || item?.key || item?.name || "",
        value: item?.value || item?.text || "",
      }))
      .filter((item) => item.label && item.value);
  }

  if (typeof extra === "object") {
    return Object.entries(extra)
      .map(([label, value]) => ({
        label,
        value: value ?? "",
      }))
      .filter((item) => item.label && item.value);
  }

  return [];
}

function getProjectDetailRows(details = {}) {
  const fixedRows = [
    { label: "Location", value: details?.location },
    { label: "Type", value: details?.type },
    { label: "Area", value: details?.area },
    { label: "Year Built", value: details?.year_built },
  ].filter((item) => item.value);

  const extraRows = getDetailExtraRows(details?.extra);

  return [...fixedRows, ...extraRows];
}

export default function ProjectDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(undefined);
  const [allProjects, setAllProjects] = useState([]);
  const [tab, setTab] = useState("motivation");
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    setProject(undefined);
    setTab("motivation");
    setLightbox(null);

    publicApi
      .track({
        event_type: "project_view",
        page: `/residential-design/${slug}`,
        project_slug: slug,
      })
      .catch(() => {});

    publicApi
      .getProject(slug)
      .then(setProject)
      .catch(() => setProject(null));

    publicApi.getProjects().then(setAllProjects).catch(() => {});
  }, [slug]);

  const { prev, next } = useMemo(() => {
    if (!allProjects.length || !project) return { prev: null, next: null };

    const idx = allProjects.findIndex((p) => p.slug === project.slug);

    return {
      prev:
        idx > 0
          ? allProjects[idx - 1]
          : allProjects[allProjects.length - 1],
      next:
        idx < allProjects.length - 1
          ? allProjects[idx + 1]
          : allProjects[0],
    };
  }, [allProjects, project]);

  if (project === undefined) {
    return (
      <div className="min-h-[70svh] pt-40 max-w-[1200px] mx-auto px-5 md:px-10">
        <div className="h-6 w-40 bg-ink-graphite animate-pulse rounded-sm" />
        <div className="h-14 w-2/3 bg-ink-graphite mt-6 animate-pulse rounded-sm" />
      </div>
    );
  }

  if (project === null) {
    return (
      <div className="min-h-[70svh] pt-40 max-w-[1200px] mx-auto px-5 md:px-10">
        <p className="eyebrow">[ Project ]</p>

        <h1 className="section-headline mt-4">
          Project not found
        </h1>

        <button
          type="button"
          onClick={() => navigate("/residential-design")}
          className="btn-outline mt-8"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Residential Design
        </button>
      </div>
    );
  }

  const gallery = project.gallery || [];
  const videos = project.videos || [];
  const detailRows = getProjectDetailRows(project.details || {});

  const beforeAfter = (project.before_after || [])
    .slice()
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .filter(
      (it) =>
        (it.before_image || it.beforeImage) &&
        (it.after_image || it.afterImage)
    );

  const tabs = BASE_TABS;

  return (
    <div>
      <section className="relative h-[85svh] min-h-[560px] w-full overflow-hidden">
        <img
          src={project.cover_image}
          alt={project.title}
          className="absolute inset-0 h-full w-full object-cover"
        />

        <div className="absolute inset-0 bg-gradient-to-b from-ink-black/70 via-ink-black/40 to-ink-black" />

        <div className="relative z-10 h-full max-w-[1440px] mx-auto px-5 md:px-10 flex flex-col justify-end pb-14 md:pb-20">
          <Link
            to="/residential-design"
            className="btn-ghost self-start mb-6 link-underline"
            data-testid="project-back-btn"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            All Projects
          </Link>

          <p className="eyebrow">
            [ {project.short_label || "Residential Project"} ]
          </p>

          <h1 className="hero-headline mt-4 max-w-4xl">
            {project.title}
          </h1>

          {project.details?.location ? (
            <p className="mt-6 eyebrow-muted">
              {project.details.location}
            </p>
          ) : null}
        </div>
      </section>

      <section className="section-pad max-w-[1200px] mx-auto px-5 md:px-10">
        <div className="flex flex-wrap gap-2 border-b border-white/10 pb-1">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              data-testid={`project-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`relative px-4 md:px-5 py-3 text-[11px] md:text-[12px] uppercase tracking-[0.18em] font-medium transition-colors ${
                tab === t.key
                  ? "text-brand-yellow"
                  : "text-brand-gray hover:text-brand-white"
              }`}
            >
              {t.label}

              {tab === t.key && (
                <span className="absolute left-0 right-0 -bottom-[1px] h-[2px] bg-brand-yellow" />
              )}
            </button>
          ))}
        </div>

        <div className="mt-10 min-h-[240px]">
          {tab === "motivation" && (
            <Reveal>
              <p className="eyebrow">[ Motivation ]</p>

              <p className="body-text mt-5 text-lg max-w-3xl whitespace-pre-wrap">
                {project.motivation ||
                  "Motivation pending client confirmation."}
              </p>
            </Reveal>
          )}

          {tab === "summary" && (
            <Reveal>
              <p className="eyebrow">[ Summary ]</p>

              <div className="body-text mt-5 text-lg max-w-3xl whitespace-pre-wrap">
                {project.summary || "Summary pending client confirmation."}
              </div>
            </Reveal>
          )}

          {tab === "details" && (
            <Reveal>
              <p className="eyebrow">[ Details ]</p>

              {detailRows.length ? (
                <dl className="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-5 max-w-3xl">
                  {detailRows.map((item, index) => (
                    <DetailRow
                      key={`${item.label}-${index}`}
                      k={item.label}
                      v={item.value}
                    />
                  ))}
                </dl>
              ) : (
                <p className="body-text mt-5">
                  Details pending client confirmation.
                </p>
              )}
            </Reveal>
          )}

          {tab === "design_team" && (
            <Reveal>
              <p className="eyebrow">[ Design Team ]</p>

              <ul className="mt-6 divide-y divide-white/8 max-w-3xl border-t border-white/8">
                {(project.design_team || []).length === 0 && (
                  <li className="py-6 body-text">
                    Design team pending client confirmation.
                  </li>
                )}

                {(project.design_team || []).map((m, i) => (
                  <li
                    key={`${m.role}-${m.name}-${i}`}
                    className="py-4 flex items-baseline justify-between gap-6"
                  >
                    <span className="eyebrow-muted">
                      {m.role}
                    </span>

                    <span className="text-brand-white font-medium">
                      {m.name}
                    </span>
                  </li>
                ))}
              </ul>
            </Reveal>
          )}
        </div>
      </section>

      {/* Gallery */}
      <section className="section-pad-sm max-w-[1440px] mx-auto px-5 md:px-10 border-t border-white/6">
        <Reveal>
          <p className="eyebrow">[ Gallery ]</p>

          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {gallery.length === 0 && (
              <p className="body-text col-span-full">
                Gallery pending image uploads.
              </p>
            )}

            {gallery.map((g, i) => (
              <button
                key={g.id || i}
                type="button"
                onClick={() => setLightbox(i)}
                className="relative aspect-[4/3] overflow-hidden bg-ink-graphite image-hover-zoom"
                data-testid={`project-gallery-${i}`}
              >
                <img
                  src={g.url}
                  alt={g.alt || `${project.title} gallery image ${i + 1}`}
                  loading="lazy"
                  className="h-full w-full object-cover"
                />

                <div className="absolute inset-0 bg-black/0 hover:bg-black/20 transition-colors" />

                {g.caption ? (
                  <span className="absolute bottom-3 left-3 right-3 text-xs text-brand-white/85 bg-ink-black/55 px-3 py-2 text-left">
                    {g.caption}
                  </span>
                ) : null}
              </button>
            ))}
          </div>
        </Reveal>
      </section>

      {/* Before / After - placed after Gallery */}
      {beforeAfter.length > 0 && (
        <section className="section-pad-sm max-w-[1200px] mx-auto px-5 md:px-10 border-t border-white/6">
          <Reveal>
            <p className="eyebrow">[ Before / After ]</p>

            <h2 className="section-headline mt-4 max-w-3xl">
              Drag to compare the transformation.
            </h2>
          </Reveal>

          <Reveal delay={90} className="mt-8">
            <BeforeAfterSlider items={beforeAfter} />
          </Reveal>
        </section>
      )}

      {videos.length ? (
        <section className="section-pad-sm max-w-[1440px] mx-auto px-5 md:px-10">
          <Reveal>
            <p className="eyebrow">[ Videos ]</p>

            <div className="mt-6 grid md:grid-cols-2 gap-4">
              {videos.map((video, i) => (
                <div key={`${video}-${i}`} className="surface overflow-hidden">
                  <video
                    src={video}
                    controls
                    preload="metadata"
                    className="aspect-video w-full bg-ink-graphite object-cover"
                  />

                  <div className="p-4">
                    <p className="eyebrow-muted">
                      Video {String(i + 1).padStart(2, "0")}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </section>
      ) : null}

      {/* Prev / Next */}
      <section className="border-t border-white/6">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-10 md:py-14 flex flex-wrap items-center justify-between gap-6">
          {prev && (
            <Link
              to={`/residential-design/${prev.slug}`}
              className="group flex items-center gap-4"
              data-testid="project-prev"
            >
              <ChevronLeft className="h-5 w-5 text-brand-yellow" />

              <div>
                <p className="eyebrow-muted">Previous</p>

                <p className="headline text-lg md:text-xl group-hover:text-brand-yellow transition-colors">
                  {prev.title}
                </p>
              </div>
            </Link>
          )}

          <Link to="/contact" className="btn-primary">
            Start a Project
            <ArrowRight className="h-4 w-4" />
          </Link>

          {next && (
            <Link
              to={`/residential-design/${next.slug}`}
              className="group flex items-center gap-4 text-right"
              data-testid="project-next"
            >
              <div>
                <p className="eyebrow-muted">Next</p>

                <p className="headline text-lg md:text-xl group-hover:text-brand-yellow transition-colors">
                  {next.title}
                </p>
              </div>

              <ChevronRight className="h-5 w-5 text-brand-yellow" />
            </Link>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {lightbox !== null && gallery[lightbox] && (
        <div
          className="fixed inset-0 z-[70] bg-ink-black/95 backdrop-blur-md flex flex-col animate-fade-in"
          onClick={() => setLightbox(null)}
        >
          <button
            type="button"
            onClick={() => setLightbox(null)}
            className="absolute top-5 right-5 md:top-8 md:right-8 h-10 w-10 grid place-items-center text-brand-white hover:text-brand-yellow"
            aria-label="Close"
          >
            <X className="h-6 w-6" />
          </button>

          <div className="flex-1 grid place-items-center px-5 md:px-16">
            <img
              src={gallery[lightbox].url}
              alt={gallery[lightbox].alt || ""}
              className="max-h-[80vh] max-w-full object-contain shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          <div className="pb-10 pt-4 flex items-center justify-center gap-6">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setLightbox((i) => (i - 1 + gallery.length) % gallery.length);
              }}
              className="h-11 w-11 grid place-items-center border border-white/25 hover:border-brand-yellow hover:text-brand-yellow"
              aria-label="Previous image"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>

            <p className="eyebrow-muted">
              {lightbox + 1} / {gallery.length}
            </p>

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setLightbox((i) => (i + 1) % gallery.length);
              }}
              className="h-11 w-11 grid place-items-center border border-white/25 hover:border-brand-yellow hover:text-brand-yellow"
              aria-label="Next image"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailRow({ k, v }) {
  if (!v) return null;

  return (
    <div className="flex items-baseline gap-4 border-b border-white/8 pb-3">
      <dt className="eyebrow-muted min-w-28">
        {k}
      </dt>

      <dd className="text-brand-white font-medium">
        {v}
      </dd>
    </div>
  );
}